/**
 * Undo/redo.
 *
 * Snapshot-based: each edit stores a deep copy of the document's entity list.
 * The original keeps an element-level undo list (IDW_LIST_UNDO) which is more
 * memory-efficient, but snapshots are far harder to get subtly wrong, and a
 * board of a few thousand primitives copies in well under a frame.
 *
 * The depth cap exists so a long session cannot grow without bound; CIRCAD
 * advertised 300 levels, and we match that.
 */

import type { CadDocument, Entity } from '@sircad/core/model.ts';

interface Snapshot {
  label: string;
  entities: Entity[];
}

const MAX_DEPTH = 300;

export class History {
  private past: Snapshot[] = [];
  private future: Snapshot[] = [];
  private pending: Snapshot | null = null;

  /**
   * Record the state *before* an edit. Safe to call repeatedly within one
   * gesture — only the first call of a pending group is kept.
   */
  begin(doc: CadDocument, label: string): void {
    if (this.pending) return;
    this.pending = { label, entities: structuredClone(doc.entities) };
  }

  /** Close the pending group. Does nothing if `begin` was never called. */
  commit(): void {
    if (!this.pending) return;
    this.past.push(this.pending);
    this.pending = null;
    if (this.past.length > MAX_DEPTH) this.past.shift();
    // Any new edit invalidates the redo branch.
    this.future.length = 0;
  }

  /** Discard a pending group without recording it, e.g. a cancelled drag. */
  abandon(): void {
    this.pending = null;
  }

  /**
   * Revert the document to the pending snapshot and discard it.
   *
   * Used to cancel an operation that mutated the document live before it was
   * committed — the vertex tool splits a line on the first click and must be
   * able to undo that if the drag is abandoned.
   */
  rollback(doc: CadDocument): boolean {
    if (!this.pending) return false;
    doc.entities = this.pending.entities;
    this.pending = null;
    return true;
  }

  /**
   * How much has been recorded, so a gesture can be wound back to here later.
   *
   * Paired with {@link revertTo}. A carried item is the reason: it moves the
   * drawing as it goes and can be turned, flipped and scaled on the way, each
   * of which records an edit of its own — and Escape has to undo the whole
   * gesture, not the last step of it. Reversing the displacement by arithmetic,
   * which is what this used to rest on, could only ever put back the part of it
   * that was a displacement.
   */
  get mark(): number {
    return this.past.length;
  }

  /**
   * Wind the document back to a {@link mark}, discarding everything since.
   *
   * Not an undo: the edits are removed rather than pushed onto the redo branch,
   * because an abandoned gesture is one that never happened, and offering to
   * redo it would be offering to redo something the operator has just said they
   * did not want. Any group still open is abandoned with it.
   */
  revertTo(doc: CadDocument, mark: number): boolean {
    // Nothing committed since the mark, so an open group is the whole of it.
    if (this.past.length <= mark) return this.rollback(doc);

    this.pending = null;
    // `past[i]` holds the state *before* edit `i`, so the mark's own snapshot
    // is the state as it stood when the mark was taken.
    doc.entities = this.past[mark]!.entities;
    this.past.length = mark;
    this.future.length = 0;
    return true;
  }

  canUndo(): boolean {
    return this.past.length > 0;
  }

  canRedo(): boolean {
    return this.future.length > 0;
  }

  undo(doc: CadDocument): string | null {
    const snap = this.past.pop();
    if (!snap) return null;
    this.future.push({ label: snap.label, entities: structuredClone(doc.entities) });
    doc.entities = snap.entities;
    return snap.label;
  }

  redo(doc: CadDocument): string | null {
    const snap = this.future.pop();
    if (!snap) return null;
    this.past.push({ label: snap.label, entities: structuredClone(doc.entities) });
    doc.entities = snap.entities;
    return snap.label;
  }

  /**
   * Un-Do everything on the stack, in one go.
   *
   * "All operations since the last time the Un-Do stack was purged", as the
   * original puts it. Done by walking the stack rather than by jumping to its
   * oldest state, so every step lands in the Re-Do stack exactly as it would
   * have if you had pressed Un-Do that many times — which is what makes Re-Do
   * All the inverse of it rather than a dead end.
   *
   * Returns how many steps it took back.
   */
  undoAll(doc: CadDocument): number {
    let n = 0;
    while (this.undo(doc) !== null) n++;
    return n;
  }

  /** Re-Do everything that was undone, and put it all back on the Un-Do stack. */
  redoAll(doc: CadDocument): number {
    let n = 0;
    while (this.redo(doc) !== null) n++;
    return n;
  }

  /**
   * Throw the Re-Do stack away.
   *
   * The original offers this as a command of its own, and the reason is worth
   * keeping: it happens automatically the moment you draw anything after
   * undoing, and doing it deliberately is how you say "that branch is not
   * coming back" before you carry on.
   *
   * Returns how many steps were discarded.
   */
  purgeRedo(): number {
    const n = this.future.length;
    this.future.length = 0;
    return n;
  }

  /**
   * Throw the Un-Do stack away, "making it impossible to accidentally Un-Do any
   * of the previous operations".
   *
   * The pending snapshot goes too. A half-open edit whose starting state has
   * been discarded would commit onto a stack that no longer holds what it began
   * from, which is worse than having no stack at all.
   */
  purgeUndo(): number {
    const n = this.past.length;
    this.past.length = 0;
    this.pending = null;
    return n;
  }

  clear(): void {
    this.past.length = 0;
    this.future.length = 0;
    this.pending = null;
  }

  /** How much there is to Re-Do. */
  get redoDepth(): number {
    return this.future.length;
  }

  get depth(): number {
    return this.past.length;
  }
}
